import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';
import MainLayout from '../../components/layouts/MainLayout';
import UserHttpService from '../../api/UserHttpService';
import LoadingOverlay from '../../components/LoadingOverlay';
import StorageService from '../../services/StorageService';
import { IMaskInput } from 'react-imask';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BankAccountHttpService from '../../api/BankAccountHttpService';

const CpfMask = React.forwardRef(function CpfMask(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="000.000.000-00"
      definitions={{
        '#': /[1-9]/,
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value: value.replace(/[^0-9]/g, '') } })}
      overwrite
    />
  );
});

const CellphoneMask = React.forwardRef(function CellphoneMask(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="(00) 00000-0000"
      definitions={{
        '#': /[1-9]/,
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value: value.replace(/[^0-9]/g, '') } })}
      overwrite
    />
  );
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function UserProfile() {
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    phone: '',
    email: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    photo_document: null,
    photo_with_document: null,
  });
  const [bankData, setBankData] = useState({
    code: '',
    agency: '',
    account_number: '',
    pix: '',
  });
  const [existingBankData, setExistingBankData] = useState(null);
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formattedCpf, setFormattedCpf] = useState('');
  const [formattedCellphone, setFormattedCellphone] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [bankCodes, setBankCodes] = useState([]);
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/user-home');
  };

  useEffect(() => {
    loadUserData();
    loadBankCodes();
    loadBankAccount();
  }, []);

  const loadUserData = () => {
    const userData = StorageService.getAuth()?.user;
    if (userData) {
      setFormData({
        name: userData.name || '',
        cpf: userData.cpf || '',
        phone: userData.phone || '',
        email: userData.email || '',
        street: userData.street || '',
        number: userData.number || '',
        complement: userData.complement || '',
        neighborhood: userData.neighborhood || '',
        city: userData.city || '',
        state: userData.state || '',
        zipCode: userData.zipCode || '',
        id: userData.id
      });
      setFormattedCpf(formatCPF(userData.cpf || ''));
      setFormattedCellphone(formatCellphone(userData.phone || ''));
    }
  };

  const loadBankCodes = async () => {
    try {
      const codes = await BankAccountHttpService.getBankCodes();
      setBankCodes(codes);
    } catch (error) {
      setError('Erro ao carregar lista de bancos');
    }
  };

  const loadBankAccount = async () => {
    try {
      const data = await BankAccountHttpService.getBankAccount();
      if (data && data.id) {
        setExistingBankData(data);
        setBankData({
          code: data.code,
          agency: data.agency,
          account_number: data.account_number,
          pix: data.pix,
        });
      }
    } catch (error) {
      setError('Erro ao carregar dados bancários');
    }
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatCellphone = (phone) => {
    if (!phone) return '';
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Atualiza o valor formatado para exibição
    if (name === 'cpf') {
      setFormattedCpf(value);
      setFormData(prev => ({
        ...prev,
        cpf: value.replace(/[^0-9]/g, '')
      }));
    } else if (name === 'phone') {
      setFormattedCellphone(value);
      setFormData(prev => ({
        ...prev,
        phone: value.replace(/[^0-9]/g, '')
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (event, field) => {
    const file = event.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [field]: file
      }));
    }
  };

  const handleBankDataChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.cpf || formData.cpf.length !== 11) {
      setError('CPF inválido');
      return false;
    }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Email inválido');
      return false;
    }
    if (formData.phone && formData.phone.length !== 11) {
      setError('Celular inválido');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const dataToSend = {
        ...formData,
        cpf: formData.cpf.replace(/[^0-9]/g, ''),
        phone: formData.phone ? formData.phone.replace(/[^0-9]/g, '') : '',
      };

      const response = await UserHttpService.updateUserData(dataToSend);
      setSuccess('Dados atualizados com sucesso!');
      
      // Atualizar os dados no storage
      const auth = StorageService.getAuth();
      if (auth) {
        const updatedUser = { 
          ...auth.user, 
          ...dataToSend,
          // Não salvamos os arquivos no storage, apenas os outros dados
          photo_document: undefined,
          photo_with_document: undefined
        };
        StorageService.saveAuth({ ...auth, user: updatedUser });
      }

      // Limpar os campos de arquivo após o sucesso
      setFormData(prev => ({
        ...prev,
        photo_document: null,
        photo_with_document: null
      }));

      setTimeout(() => {
        navigate('/user-home');
      }, 1000);

    } catch (error) {
      console.error('Error updating user data:', error);
      setError(error.message || 'Erro ao atualizar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await BankAccountHttpService.createOrUpdateBankAccount(bankData);
      setSuccess('Dados bancários atualizados com sucesso!');
      await loadBankAccount();
      setIsEditingBank(false);
    } catch (error) {
      setError(error.message || 'Erro ao atualizar dados bancários');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setError('');
  };

  const handleCloseSuccess = () => {
    setSuccess('');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const renderDocumentsTab = () => {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Documentos
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="document-photo"
                type="file"
                onChange={(e) => handleFileChange(e, 'photo_document')}
              />
              <label htmlFor="document-photo">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  sx={{ height: '200px', display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  {formData.photo_document ? (
                    <>
                      <img
                        src={URL.createObjectURL(formData.photo_document)}
                        alt="Documento"
                        style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                      <Typography>Alterar foto do documento</Typography>
                    </>
                  ) : (
                    <>
                      <Typography>Foto do Documento</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Clique para fazer upload
                      </Typography>
                    </>
                  )}
                </Button>
              </label>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="selfie-document"
                type="file"
                onChange={(e) => handleFileChange(e, 'photo_with_document')}
              />
              <label htmlFor="selfie-document">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  sx={{ height: '200px', display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  {formData.photo_with_document ? (
                    <>
                      <img
                        src={URL.createObjectURL(formData.photo_with_document)}
                        alt="Selfie com documento"
                        style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                      <Typography>Alterar selfie com documento</Typography>
                    </>
                  ) : (
                    <>
                      <Typography>Selfie com Documento</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Clique para fazer upload
                      </Typography>
                    </>
                  )}
                </Button>
              </label>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const getBankName = (code) => {
    const bank = bankCodes.find(b => b.codigo === code);
    return bank ? bank.banco : code;
  };

  return (
    <MainLayout>
      <LoadingOverlay open={loading} />
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Typography variant="h5" component="h1">
            Meu Perfil
          </Typography>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={handleTabChange} aria-label="profile tabs">
              <Tab label="Dados Pessoais" />
              <Tab label="Endereço" />
              <Tab label="Documentos" />
              <Tab label="Dados Bancários" />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Nome"
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Celular"
                name="phone"
                value={formattedCellphone}
                onChange={handleChange}
                margin="normal"
                InputProps={{
                  inputComponent: CellphoneMask,
                }}
              />

              <TextField
                fullWidth
                label="CPF"
                name="cpf"
                value={formattedCpf}
                disabled
                onChange={handleChange}
                margin="normal"
                required
                InputProps={{
                  inputComponent: CpfMask,
                }}
              />
            </form>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="CEP"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Rua"
                name="street"
                value={formData.street}
                onChange={handleChange}
                margin="normal"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Número"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Complemento"
                  name="complement"
                  value={formData.complement}
                  onChange={handleChange}
                  margin="normal"
                />
              </Box>
              <TextField
                fullWidth
                label="Bairro"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleChange}
                margin="normal"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Cidade"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  margin="normal"
                />
                <TextField
                  label="Estado"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  margin="normal"
                />
              </Box>
            </form>
          </TabPanel>

          <TabPanel value={currentTab} index={2}>
            {renderDocumentsTab()}
          </TabPanel>

          <TabPanel value={currentTab} index={3}>
            {existingBankData && !isEditingBank ? (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Dados Bancários
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Banco
                    </Typography>
                    <Typography variant="body1">
                      {getBankName(existingBankData.code)} ({existingBankData.code})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Agência
                    </Typography>
                    <Typography variant="body1">
                      {existingBankData.agency}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Número da Conta
                    </Typography>
                    <Typography variant="body1">
                      {existingBankData.account_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Chave PIX
                    </Typography>
                    <Typography variant="body1">
                      {existingBankData.pix}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setIsEditingBank(true)}
                      sx={{ mt: 2 }}
                    >
                      Editar Dados Bancários
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <form onSubmit={handleBankSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Banco"
                      name="code"
                      value={bankData.code}
                      onChange={handleBankDataChange}
                      required
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="">Selecione um banco</option>
                      {bankCodes.map((bank) => (
                        <option key={bank.codigo} value={bank.codigo}>
                          {bank.banco} ({bank.codigo})
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Agência"
                      name="agency"
                      value={bankData.agency}
                      onChange={handleBankDataChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Número da Conta"
                      name="account_number"
                      value={bankData.account_number}
                      onChange={handleBankDataChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Chave PIX"
                      name="pix"
                      value={bankData.pix}
                      onChange={handleBankDataChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                      >
                        {existingBankData ? 'Atualizar' : 'Salvar'} Dados Bancários
                      </Button>
                      {isEditingBank && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => {
                            setIsEditingBank(false);
                            setBankData({
                              code: existingBankData.code,
                              agency: existingBankData.agency,
                              account_number: existingBankData.account_number,
                              pix: existingBankData.pix,
                            });
                          }}
                        >
                          Cancelar
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </TabPanel>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              Salvar
            </Button>
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
}

export default UserProfile;
